/**
 * Axios singleton para tax-partners-frontend.
 *
 * Auth: JWT bearer (mismo flow que tax-admin-frontend).
 * Token en memoria (no localStorage por XSS).
 *
 * Endpoints expuestos por taxor backend bajo /api/v1/admin/* (JWT) y
 * /api/v1/onboarding/* (público) y /api/v1/partners/me/* (JWT).
 */

import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { toast } from 'sonner'
import { APP } from './constants'

export const api = axios.create({
  baseURL: APP.apiUrl,
  timeout: 30_000,
  withCredentials: true,
})

// ─── Token storage en memoria ─────────────────────────────────────
let accessToken: string | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

export function hasValidAccessToken(): boolean {
  if (!accessToken) return false
  const parts = accessToken.split('.')
  if (parts.length !== 3) return false
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    if (typeof payload?.exp !== 'number') return false
    return payload.exp > Math.floor(Date.now() / 1000)
  } catch {
    return false
  }
}

// ─── Interceptors ─────────────────────────────────────────────────
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expirado/inválido — limpiar y redirigir a login.
      setAccessToken(null)
      if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
        window.location.href = '/login'
      }
    }
    if (error.response?.status === 403) {
      // Autorización insuficiente (rol o tenant equivocado). NO redirigir
      // (evita loops con /login): avisar y dejar que la page maneje el error.
      const data = error.response.data as { message?: string } | undefined
      toast.error(
        data?.message ?? 'No tienes acceso a este recurso (organización activa no coincide)',
      )
    }
    return Promise.reject(error)
  },
)

// ─── Tipos ────────────────────────────────────────────────────────

export type ContractKind = 'terms' | 'mandate'

export interface Contract {
  kind: ContractKind
  version: string
  html: string
  html_hash: string
}

export interface OnboardingStatus {
  terms_up_to_date: boolean
  mandate_up_to_date: boolean
  current_terms_version: string
  current_mandate_version: string
  blocked_reason?: string
}

export interface AcceptContractResponse {
  acceptance_id: string
  kind: ContractKind
  contract_version: string
  contract_html_hash: string
  accepted_at: string
  next_step?: string
}

export interface RegisterERPPartnerRequest {
  name: string
  legal_name?: string
  nit: string
  contact_email: string
  allowed_emitter_nits?: string[]
  webhook_url?: string
  rate_limit_per_min?: number
}

export interface RegisterERPPartnerResponse {
  client_id: string
  name: string
  nit: string
  api_key: string
  hmac_secret: string
  api_key_prefix: string
  warning: string
  next_step: string
  contracts_to_accept: ContractKind[]
}

export interface QuotaUsage {
  docs_per_month: { current: number; limit: number; percent_used: number; reset_at: string }
  webhook_deliveries_per_day: { current: number; limit: number; percent_used: number; reset_at: string }
  storage_bytes: { current: number; limit: number; percent_used: number }
}

export interface CertInfo {
  id: string
  tax_client_id: string
  serial_hex: string
  subject_cn: string
  subject_nit?: string
  issuer_cn: string
  issuer_normalized: string
  valid_from: string
  valid_to: string
  days_until_expiration: number
  key_algorithm: string
  status: string
  uploaded_at: string
  archived_at?: string
  archived_reason?: string
}

export interface CertResponse {
  has_byo_cert: boolean
  cert?: CertInfo
  message?: string
}

export interface MandateAcceptance {
  id: string
  contract_kind: string
  contract_version: string
  accepted_at: string
  ip_address: string
  user_agent: string
  this_hash: string
}

// ─── API methods ──────────────────────────────────────────────────

export const onboardingApi = {
  getContract: (kind: ContractKind) =>
    api.get<Contract>(`/api/v1/onboarding/contracts/${kind}`).then((r) => r.data),

  registerERPPartner: (body: RegisterERPPartnerRequest) =>
    api
      .post<RegisterERPPartnerResponse>('/api/v1/onboarding/erp-partner', body)
      .then((r) => r.data),

  acceptContract: (kind: ContractKind) =>
    api
      .post<AcceptContractResponse>('/api/v1/admin/onboarding/accept', { kind })
      .then((r) => r.data),

  status: () =>
    api.get<OnboardingStatus>('/api/v1/admin/onboarding/status').then((r) => r.data),
}

export type DataExportKind = 'full' | 'accounting' | 'audit' | 'personal'

export interface DataExportRequestResponse {
  export_id: string
  status: string
  kind: string
  poll_url: string
  message: string
  max_wait_min: number
}

export interface DataExportStatus {
  id: string
  status: 'pending' | 'processing' | 'ready' | 'failed' | string
  export_kind: string
  signed_url: string | null
  signed_url_expires_at: string | null
  requested_at: string | null
  ready_at: string | null
  size_bytes: number | null
}

// El backend serializa la fila con los nombres de campo Go (sin json tags):
// ID, Status, ExportKind, SignedURL, SignedURLExpiresAt, RequestedAt,
// ReadyAt, SizeBytes. Normalizamos a snake_case y toleramos ambas formas
// por si el backend agrega tags después.
function normalizeExportStatus(raw: Record<string, unknown>): DataExportStatus {
  const pick = <T>(...keys: string[]): T | null => {
    for (const k of keys) {
      if (raw[k] !== undefined && raw[k] !== null) return raw[k] as T
    }
    return null
  }
  return {
    id: pick<string>('id', 'ID') ?? '',
    status: pick<string>('status', 'Status') ?? 'pending',
    export_kind: pick<string>('export_kind', 'ExportKind') ?? '',
    signed_url: pick<string>('signed_url', 'SignedURL'),
    signed_url_expires_at: pick<string>('signed_url_expires_at', 'SignedURLExpiresAt'),
    requested_at: pick<string>('requested_at', 'RequestedAt'),
    ready_at: pick<string>('ready_at', 'ReadyAt'),
    size_bytes: pick<number>('size_bytes', 'SizeBytes'),
  }
}

// NOTA: el backend monta el self-service de partners bajo el grupo admin:
// /api/v1/admin/partners/me/* (taxor/internal/server/server.go). El JWT
// resuelve el tenant (org_id) — no se pasa client_id.
export const partnerApi = {
  usage: () => api.get<QuotaUsage>('/api/v1/admin/partners/me/usage').then((r) => r.data),

  regenerateApiKeys: () =>
    api
      .post<{ api_key: string; hmac_secret: string; warning: string }>(
        '/api/v1/admin/partners/me/api-keys/regenerate',
      )
      .then((r) => r.data),

  auditHistory: (limit = 100) =>
    api
      .get<{ items: MandateAcceptance[]; count: number }>(
        `/api/v1/admin/partners/me/audit-history?limit=${limit}`,
      )
      .then((r) => r.data),

  requestDataExport: (kind: DataExportKind, legalReason?: string) =>
    api
      .post<DataExportRequestResponse>('/api/v1/admin/partners/me/data-export-request', {
        kind,
        legal_reason: legalReason,
      })
      .then((r) => r.data),

  getDataExportStatus: (id: string) =>
    api
      .get<Record<string, unknown>>(`/api/v1/admin/partners/me/data-export-request/${id}`)
      .then((r) => normalizeExportStatus(r.data)),
}

// ─── tax-account (IDP) — identidad + org activa ───────────────────
//
// El portal necesita el id de la org activa (== tax_client_id) para los
// endpoints admin con :client_id en el path (p. ej. cert BYO). Mismo
// patrón que tax-facturador-frontend: GET /auth/me contra tax-account.

export const accountApi = axios.create({
  baseURL: APP.accountUrl,
  timeout: 30_000,
  withCredentials: true,
})

accountApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

export interface MeActiveOrg {
  id: string
  tax_id: string
  razon_social: string
  nombre_comercial: string | null
  role: string | null
}

export interface MeResponse {
  person_id: string
  email: string
  full_name: string
  active_org: MeActiveOrg | null
}

export const authApi = {
  me: () => accountApi.get<MeResponse>('/api/v1/auth/me').then((r) => r.data),
}

export const certApi = {
  // El client_id se resuelve del JWT (org_id) en el backend.
  // Aquí se pasa explícito para reutilizar el endpoint admin.
  get: (clientId: string) =>
    api.get<CertResponse>(`/api/v1/admin/tax-clients/${clientId}/cert`).then((r) => r.data),

  upload: (clientId: string, file: File, password: string, expectedNIT?: string) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('password', password)
    if (expectedNIT) fd.append('expected_nit', expectedNIT)
    return api
      .post<CertInfo>(`/api/v1/admin/tax-clients/${clientId}/cert/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },

  archive: (clientId: string, reason: string) =>
    api
      .delete(`/api/v1/admin/tax-clients/${clientId}/cert`, { data: { reason } })
      .then((r) => r.data),

  history: (clientId: string) =>
    api
      .get<{ items: CertInfo[]; count: number }>(
        `/api/v1/admin/tax-clients/${clientId}/cert/history`,
      )
      .then((r) => r.data),
}
